import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, BookOpen, Download, ArrowRight, Star, Pen, Smartphone, Package, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import Footer from "@/components/Footer";
import CountdownTimer from "@/components/CountdownTimer";

import { API } from "@/lib/api";

const BOOK_COVER = "/images/ebook-cover.jpg";

const PRODUCTS = [
  {
    id: "digital",
    name: "eBook",
    subtitle: "Digital Download \u2014 Read Anywhere",
    price: "$14.99",
    currency: "AUD",
    icon: Smartphone,
    features: ["Instant digital delivery via email", "EPUB format", "Read on any device"],
    highlight: false,
  },
  {
    id: "early_bird",
    name: "Physical Copy",
    subtitle: "First Edition Paperback",
    price: "$29.99",
    currency: "AUD",
    icon: Package,
    features: ["First edition paperback", "Ships from Australia", "Available now"],
    highlight: true,
    badge: "Most Popular",
    shippingNote: "+ shipping",
  },
];

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const type = params.get("type") || "chapter";
  const isChapter = type === "chapter";
  const isGuide = type === "guide";
  const isBoth = type === "both";
  const [loadingId, setLoadingId] = useState(null);
  const [otherClaimed, setOtherClaimed] = useState(false);

  const handleCheckout = async (packageId) => {
    setLoadingId(packageId);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin,
        email: "",
        name: "",
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoadingId(null);
    }
  };

  // Claim the other lead magnet (already have email in DB, just navigate)
  const handleClaimOther = () => {
    setOtherClaimed(true);
  };

  const showChapterDownload = isChapter || isBoth || otherClaimed;
  const showGuideDownload = isGuide || isBoth || otherClaimed;

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="thank-you-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white" data-testid="thankyou-nav-logo">FYB</Link>
        </div>
      </nav>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Success Confirmation */}
          <div className="text-center space-y-6 mb-16">
            <div className="flex justify-center animate-fade-in">
              <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
              </div>
            </div>
            <div className="opacity-0 animate-fade-in-up stagger-1">
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-3">
                {isBoth ? "Your Free Resources Are Ready" : isChapter ? "Your Free Chapter Is Ready" : "Your Mini-Guide Is Ready"}
              </h1>
              <p className="text-zinc-400 text-base sm:text-lg max-w-xl mx-auto">
                {isBoth
                  ? "Download both resources below and start your breakthrough today."
                  : isChapter
                  ? "Download the prologue of The Forty Year Bender right now. The story starts here."
                  : "Download the CLARITY Protocol Mini-Guide and start your 30-day nervous system reset today."}
              </p>
            </div>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up stagger-2">
              {showChapterDownload && (
                <a
                  href="/downloads/TheFortyYearBender_Prologue.pdf"
                  download="TheFortyYearBender_Prologue_Chris_Merlo.pdf"
                  className="inline-flex items-center gap-3 btn-gold px-8 py-4 text-sm tracking-[0.1em] uppercase rounded-sm"
                  data-testid="download-chapter-button"
                >
                  <FileDown className="w-5 h-5" />
                  Download Free Chapter
                </a>
              )}
              {showGuideDownload && (
                <a
                  href="/downloads/CLARITY_Protocol_MiniGuide.pdf"
                  download="CLARITY_Protocol_MiniGuide_Chris_Merlo.pdf"
                  className="inline-flex items-center gap-3 btn-gold px-8 py-4 text-sm tracking-[0.1em] uppercase rounded-sm"
                  data-testid="download-guide-button"
                >
                  <FileDown className="w-5 h-5" />
                  Download Mini-Guide
                </a>
              )}
            </div>
          </div>

          <div className="gold-line mb-16" />

          {/* Upsell Section */}
          <div className="text-center mb-12 opacity-0 animate-fade-in-up stagger-2">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-3">While You're Here</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white mb-4">
              Get The Full Book &mdash; Choose Your Edition
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              You've read a taste. Now get the complete story. Three ways to experience
              The Forty Year Bender &mdash; including a personally signed copy with a
              handwritten note from Chris himself.
            </p>
          </div>

          {/* Countdown */}
          <div className="flex justify-center mb-12 opacity-0 animate-fade-in-up stagger-3">
            <div className="text-center space-y-3">
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-500">Pre-order before launch</p>
              <CountdownTimer />
            </div>
          </div>

          {/* Product Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 opacity-0 animate-fade-in-up stagger-4">
            {PRODUCTS.map((product) => {
              const Icon = product.icon;
              return (
                <div
                  key={product.id}
                  className={`relative border rounded-sm p-6 sm:p-8 space-y-5 transition-all duration-300 hover:translate-y-[-4px] ${
                    product.highlight
                      ? "border-[#D4AF37] bg-[#121212] shadow-lg shadow-[#D4AF37]/10"
                      : "border-white/10 bg-[#121212] hover:border-[#D4AF37]/50"
                  }`}
                  data-testid={`product-card-${product.id}`}
                >
                  {product.badge && (
                    <div className={`absolute -top-3 left-6 px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase rounded-sm ${
                      product.highlight ? "bg-[#D4AF37] text-[#050505]" : "bg-white/10 text-[#D4AF37] border border-[#D4AF37]/30"
                    }`}>
                      {product.badge}
                    </div>
                  )}

                  <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>

                  <div>
                    <h3 className="font-heading text-xl tracking-tight uppercase text-white">{product.name}</h3>
                    <p className="text-xs text-zinc-500 mt-1">{product.subtitle}</p>
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-4xl text-[#D4AF37]">{product.price}</span>
                    {product.originalPrice && (
                      <span className="price-strike text-base text-zinc-600">{product.originalPrice}</span>
                    )}
                  </div>
                  {product.savings && (
                    <p className="text-xs text-[#D4AF37] font-semibold">{product.savings}</p>
                  )}

                  <Separator className="bg-white/10" />

                  <ul className="space-y-2">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-zinc-300">
                        <ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleCheckout(product.id)}
                    disabled={loadingId === product.id}
                    className={`w-full h-12 rounded-sm text-xs tracking-[0.15em] uppercase ${
                      product.highlight
                        ? "btn-gold animate-pulse-gold"
                        : "border border-white/20 bg-transparent text-white hover:bg-white/5 hover:border-[#D4AF37]"
                    }`}
                    data-testid={`checkout-${product.id}-button`}
                  >
                    {loadingId === product.id ? "Redirecting to checkout..." : `Get ${product.name}`}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Book Cover + Trust */}
          <div className="flex flex-col items-center gap-6 opacity-0 animate-fade-in-up stagger-5">
            <img src={BOOK_COVER} alt="The Forty Year Bender by Chris Merlo - Find Your Breakthrough FYB - CLARITY Protocol" title="The Forty Year Bender by Chris Merlo" className="w-40 shadow-xl shadow-black/50" />
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => (
                <Star key={s} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <p className="text-zinc-500 text-sm text-center max-w-md">
              Secure checkout powered by Stripe. Your payment information is encrypted and protected.
            </p>
          </div>

          {/* Offer the other lead magnet directly */}
          {!isBoth && !otherClaimed && (
            <div className="text-center mt-12 opacity-0 animate-fade-in-up stagger-6">
              <div className="bg-[#121212] border border-white/10 p-6 rounded-sm max-w-md mx-auto space-y-4">
                <p className="text-zinc-300 text-sm font-medium">
                  {isChapter
                    ? "Also want the 30-day Clarity Protocol Mini-Guide?"
                    : "Also want to read the first chapter of the book?"}
                </p>
                <Button
                  onClick={handleClaimOther}
                  className="w-full h-11 rounded-sm text-xs tracking-[0.1em] uppercase border border-[#D4AF37]/50 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid="claim-other-resource-button"
                >
                  {isChapter ? (
                    <><Download className="w-4 h-4 mr-2" /> Yes, Give Me The Mini-Guide Too</>
                  ) : (
                    <><BookOpen className="w-4 h-4 mr-2" /> Yes, Give Me The Free Chapter Too</>
                  )}
                </Button>
                <p className="text-zinc-600 text-[10px]">Free. No extra signup needed.</p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
